import { ChangeEvent, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { yupResolver } from '@hookform/resolvers/yup';
import { Check } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Card,
  CardHeader,
  Checkbox,
  Container,
  FormControlLabel,
  InputAdornment,
  Link as MuiLink,
  SxProps,
  Theme,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import * as yup from 'yup';
import { UserInput } from '../../api/gql';
import { FormInput } from '../../components/form/FormInput';
import { useAresSearch } from '../../hooks/useAresSearch';
import { BareLayout } from '../../layout';
import { langKeys } from '../../localization/formErrors';
import { getConfig } from '../../utils/config';
import { notify } from '../../utils/notification';
import { phoneNumberSchema, vatIdOptionalSchema } from '../../utils/validationSchemas';
import { validators } from '../../utils/validators';
import { useUpdateUser } from './hooks/useUdateUser';
import { useSafeNavigation } from '../../hooks/useSafeNavigation';

const { termsAndConditionsUrl, gdprUrl } = getConfig();

export const UserRegistration = () => {
  const { t } = useTranslation();
  const { safeNavigate } = useSafeNavigation();
  const { updateUser, isPending } = useUpdateUser({
    onSuccess: () => {
      notify.positive({ message: t('registration.success') });
      safeNavigate('/registration-reminding-tone');
    },
    onError: () => {
      notify.negative({ message: t('registration.error') });
    },
  });

  const { user } = useAuth0();

  const { control, resetField, handleSubmit, getValues, formState, getFieldState, setValue } =
    useForm<UserRegistrationForm>({
      defaultValues: {
        ...defaultValues,
        firstName: user?.given_name ?? '',
        lastName: user?.family_name ?? '',
      },
      mode: 'onChange',
      resolver: yupResolver(validationSchema),
    });

  // vatId state subscribers
  const vatIdState = getFieldState('vatId', formState);
  const isAresPossible = useMemo(() => vatIdState.isDirty && !vatIdState.invalid, [vatIdState]);

  const [termsChecked, setTermsChecked] = useState(true);

  const handleTermsChange = (event: ChangeEvent<HTMLInputElement>) => {
    setTermsChecked(event.target.checked);
  };

  const {
    search,
    state: { isPending: isAresLoading },
  } = useAresSearch();

  const onSubmit = (data: UserRegistrationForm) => {
    updateUser({ input: data });
  };

  const onAresSearch = async () => {
    const vatId = getValues('vatId');

    if (!vatId) {
      // should not happen due to validation
      return;
    }

    const res = await search({
      vatId: vatId,
    });

    resetField('vatId', { defaultValue: vatId }); //reset vatId field validations to disable spamming of ARES API

    if (res.queryAres) {
      res.queryAres.name ? setValue('businessName', res.queryAres.name) : null;
      res.queryAres.taxId ? setValue('taxId', res.queryAres.taxId) : null;
      res.queryAres.streetAddress ? setValue('streetAddress', res.queryAres.streetAddress) : null;
      res.queryAres.city ? setValue('city', res.queryAres.city) : null;
      res.queryAres.country ? setValue('country', res.queryAres.country) : null;
      res.queryAres.zip ? setValue('zipCode', res.queryAres.zip) : null;

      notify.positive({ message: t('ares.prefill.success') });
      return;
    }

    notify.warning({ message: t('ares.prefill.error') });
  };

  return (
    <BareLayout>
      <Container sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Card sx={cardStyles} variant={'outlined'}>
          <CardHeader
            title={
              <Typography variant={'h1'} align={'center'}>
                {t('completionOfRegistration')}
              </Typography>
            }
          />
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box pt={4}>
              <Typography variant={'h6'}>{t('contactInfo')}</Typography>
              <Grid container spacing={2} mt={1}>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'firstName'} label={t('firstName') + '*'} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'lastName'} label={t('lastName') + '*'} />
                </Grid>
                <Grid xs={12}>
                  <FormInput control={control} name={'phoneNumber'} label={t('phone') + '*'} type={'tel'} />
                </Grid>
              </Grid>
            </Box>
            <Box pt={4}>
              <Typography variant={'h6'}>{t('billingInfo')}</Typography>
              <Grid container spacing={2} mt={1}>
                <Grid xs={12}>
                  <FormInput
                    control={control}
                    name={'vatId'}
                    label={t('vatId')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position={'end'}>
                          <LoadingButton
                            loading={isAresLoading}
                            onClick={onAresSearch}
                            variant={'outlined'}
                            disabled={!isAresPossible}
                          >
                            {t('loadFromAres')}
                          </LoadingButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'businessName'} label={t('businessName') + '*'} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'taxId'} label={t('taxId')} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'streetAddress'} label={t('streetAddress') + '*'} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'city'} label={t('city') + '*'} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'zipCode'} label={t('zipCode') + '*'} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'country'} label={t('country') + '*'} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'bankAccount'} label={t('bankAccount')} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'bankNumber'} label={t('bankNumber')} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'iban'} label={t('iban')} />
                </Grid>
                <Grid xs={12} md={6}>
                  <FormInput control={control} name={'swift'} label={t('swift')} />
                </Grid>
                <Grid xs={12}>
                  <FormControlLabel
                    checked={termsChecked}
                    control={<Checkbox onChange={handleTermsChange} />}
                    label={
                      <>
                        <Trans
                          t={t}
                          i18nKey={'termsAndConditionsConsent'}
                          components={{
                            link1: <MuiLink component={Link} to={termsAndConditionsUrl} target={'_blank'} />,
                            link2: <MuiLink component={Link} to={gdprUrl} target={'_blank'} />,
                          }}
                        />
                        {'*'}
                      </>
                    }
                  />
                </Grid>
              </Grid>
            </Box>
            <LoadingButton
              loading={isPending}
              variant={'contained'}
              fullWidth
              size={'large'}
              sx={{ mt: 6 }}
              type={'submit'}
              startIcon={<Check />}
              disabled={!termsChecked || formState.isSubmitting}
            >
              {t('completeTheRegistration')}
            </LoadingButton>
          </form>
        </Card>
      </Container>
    </BareLayout>
  );
};

// Styles
const cardStyles: SxProps<Theme> = (theme) => ({
  padding: 4,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    width: '85%',
  },
  [theme.breakpoints.up('md')]: {
    width: '80%',
  },
});

// Form definitions and validations
type UserRegistrationForm = Omit<UserInput, 'defaultRemindingTone'>;

const defaultValues: UserRegistrationForm = {
  firstName: '',
  lastName: '',
  phoneNumber: '',
  taxId: '',
  vatId: '',
  businessName: '',
  streetAddress: '',
  city: '',
  zipCode: '',
  country: 'Česká republika',
};

const validationSchema: yup.ObjectSchema<UserRegistrationForm> = yup.object({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  phoneNumber: phoneNumberSchema,
  vatId: vatIdOptionalSchema,
  businessName: yup.string().required(),
  streetAddress: yup.string().required(),
  city: yup.string().required(),
  zipCode: yup.string().required().matches(validators.postalCode, langKeys.invalid).trim(),
  country: yup.string().required(),
  taxId: yup.string(),
  bankAccount: yup.string(),
  bankNumber: yup.string(),
  iban: yup.string(),
  swift: yup.string(),
});
